<script setup lang="ts">
import type { CookieCategory, IConsentPreferences } from '@/entities/consent'
import { ElSwitch } from 'element-plus'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { CATEGORY_ORDER, useConsentStore } from '@/entities/consent'
import { useCustomToast } from '@/shared/lib'
import CButton from '@/shared/ui/CButton.vue'

/**
 * The body of the cookie preferences experience. Rendered both inside the
 * profile tab and inside the landing banner's "Customize" dialog — the two
 * contexts share a single form so the copy, spacing, and save semantics
 * never drift between them.
 *
 * GDPR notes:
 *   - "necessary" is rendered as a locked toggle with an "always on" pill
 *     so users see what they cannot disable and why
 *   - The footer announces when consent was last saved — required evidence
 *     if we ever need to prove compliance
 *   - Draft state is kept local until the user clicks Save, so closing the
 *     dialog without saving does NOT silently mutate their consent
 */

const emit = defineEmits<{
  saved: []
  withdrawn: []
}>()

const { t, locale } = useI18n()
const consentStore = useConsentStore()
const { success } = useCustomToast()

const draft = ref<IConsentPreferences>(cloneCurrentPreferences())

// Re-sync the draft when the source of truth changes externally — e.g. the
// user accepted everything from the banner in another tab, or the backend
// pushed down remote preferences after initial mount. Without this, the
// form would show stale toggles after such a change.
watch(
  () => consentStore.preferences,
  (next) => {
    draft.value = cloneFromSource(next)
  },
  { deep: true },
)

const isDirty = computed(() => {
  const current = consentStore.preferences
  return (
    draft.value.functional !== current.functional
    || draft.value.analytics !== current.analytics
    || draft.value.marketing !== current.marketing
  )
})

const lastUpdatedLabel = computed(() => {
  const raw = consentStore.decidedAt
  if (!raw)
    return t('cookie_never_decided')

  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime()))
    return t('cookie_never_decided')

  // Use Intl directly — we don't ship datetimeFormats to vue-i18n.
  const formatter = new Intl.DateTimeFormat(locale.value, {
    dateStyle: 'long',
    timeStyle: 'short',
  })
  return `${t('cookie_last_updated')}: ${formatter.format(parsed)}`
})

function cloneCurrentPreferences(): IConsentPreferences {
  return cloneFromSource(consentStore.preferences)
}

function cloneFromSource(source: IConsentPreferences): IConsentPreferences {
  return {
    necessary: true,
    functional: source.functional,
    analytics: source.analytics,
    marketing: source.marketing,
  }
}

function onToggle(category: CookieCategory, value: boolean) {
  if (category === 'necessary')
    return
  draft.value = { ...draft.value, [category]: value, necessary: true }
}

function onSave() {
  consentStore.update({
    functional: draft.value.functional,
    analytics: draft.value.analytics,
    marketing: draft.value.marketing,
  })
  success({
    title: t('cookie_preferences_saved'),
    message: lastUpdatedLabel.value,
  })
  emit('saved')
}

function onWithdrawAll() {
  consentStore.reset()
  draft.value = cloneFromSource({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
  })
  success({
    title: t('cookie_preferences_cleared'),
    message: '',
  })
  emit('withdrawn')
}

// i18n helpers kept separate to keep the template readable.
function categoryLabel(category: CookieCategory): string {
  return t(`cookie_category_${category}`)
}

function categoryDescription(category: CookieCategory): string {
  return t(`cookie_category_${category}_description`)
}
</script>

<template>
  <div class="flex flex-col gap-4 md:gap-6">
    <p class="text-sm md:text-base text-text-regular leading-relaxed">
      {{ t('cookie_preferences_description') }}
    </p>

    <ul class="flex flex-col gap-3 list-none p-0 m-0">
      <li
        v-for="category in CATEGORY_ORDER"
        :key="category"
        class="border border-border-light-3 rounded-xl p-4 md:p-5 bg-white flex flex-col sm:flex-row gap-3 sm:gap-4"
      >
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 flex-wrap">
            <span class="font-semibold text-text-primary text-base">
              {{ categoryLabel(category) }}
            </span>
            <span
              v-if="category === 'necessary'"
              class="text-xs font-medium text-text-regular bg-gray-100 rounded-full px-2 py-0.5"
            >
              {{ t('cookie_always_on') }}
            </span>
          </div>
          <p class="text-sm text-text-regular leading-relaxed mt-1">
            {{ categoryDescription(category) }}
          </p>
        </div>

        <div class="flex-shrink-0 flex items-start sm:items-center">
          <ElSwitch
            :model-value="draft[category]"
            :disabled="category === 'necessary'"
            :aria-label="categoryLabel(category)"
            @update:model-value="(val) => onToggle(category, val === true)"
          />
        </div>
      </li>
    </ul>

    <div
      class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2"
    >
      <span class="text-xs md:text-sm text-text-regular">
        {{ lastUpdatedLabel }}
      </span>

      <div class="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <CButton
          v-if="consentStore.hasDecided"
          type="default"
          class="h-10"
          @click="onWithdrawAll"
        >
          {{ t('cookie_withdraw_all') }}
        </CButton>
        <CButton
          type="primary"
          class="h-10"
          :disabled="!isDirty && consentStore.hasDecided"
          @click="onSave"
        >
          {{ t('cookie_save') }}
        </CButton>
      </div>
    </div>
  </div>
</template>
